import { BASEURL } from "../BaseUrl";

/**
 * Fetches sample records from the data processor.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {Object} data - The data to be sent with the request.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
export const fetchSampleRecordsApi = async (axios, data) => {
  return await axios.post(`${BASEURL}/data-processor/sample-view`, data, {});
};

/**
 * Fetches the list of S3 folders based on specified parameters.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} client_id - The ID of the client.
 * @param {string} engagement_name - The name of the engagement.
 * @param {string} workflow_name - The name of the workflow.
 * @param {string} timestamp_type - The type of timestamp to filter folders.
 * @param {string} aws_config_type - The AWS configuration type.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
export const AllFoldersOfS3 = async (
  axios,
  client_id,
  engagement_name,
  workflow_name,
  timestamp_type,
  aws_config_type
) => {
  return await axios.get(`${BASEURL}/data-processor/get-s3-folder-list`, {
    params: {
      client_id,
      engagement_name,
      workflow_name,
      timestamp_type,
      aws_config_type,
    },
  });
};

/**
 * Fetches the list of S3 files based on a prefix and client ID.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} prefix - The prefix to filter files.
 * @param {string} client_id - The ID of the client.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
export const AllFilesOfS3 = async (axios, prefix, client_id) => {
  return await axios.get(`${BASEURL}/data-processor/get-s3-files-list`, {
    params: { prefix, client_id },
  });
};

/**
 * Downloads a file from S3 and triggers a download in the browser.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {Object} data - The data containing file path and other parameters.
 * @returns {Promise} - A Promise that resolves when the download is complete.
 */
export const downloadFilesOfS3 = async (axios, data) => {
  await axios
    .get(`${BASEURL}/data-processor/download-file`, {
      params: data,
      responseType: "blob",
    })
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${data.file_path.split("/").at(-1)}`);
      document.body.appendChild(link);
      link.click();
    })
    .catch(async (error) => {
      if (error.response.status === 401) {
        // Handle unauthorized error (e.g., log out user)
      }

      throw error;
    });
};

/**
 * Fetches the list of headers for a specific folder.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {Object} data - The data containing folder and other parameters.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
export const getFolderHeaderList = async (axios, data) => {
  return await axios.get(`${BASEURL}/data-processor/get-folder-header-list`, {
    params: data,
  });
};

/**
 * Fetches data processor information based on workflow ID and execution mode.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} workflow_id - The ID of the workflow.
 * @param {string} execution_mode - The execution mode for the data processor.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
export const getDataProcessor = async (axios, workflow_id, execution_mode) => {
  return await axios.get(`${BASEURL}/data-processor/get-data-processor`, {
    params: { workflow_id, execution_mode },
  });
};

/**
 * Fetches the status of a specific node in the workflow.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} client_name - The name of the client.
 * @param {string} engagement_name - The name of the engagement.
 * @param {string} workflow_name - The name of the workflow.
 * @param {string} cloudEnvironment - The cloud environment to filter by.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
export const getNodeStatus = async (
  axios,
  client_name,
  engagement_name,
  workflow_name,
  cloudEnvironment
) => {
  return await axios.get(`${BASEURL}/node-status/get-step-status`, {
    params: { client_name, engagement_name, workflow_name, cloudEnvironment },
  });
};

/**
 * Creates a new data processor with the provided data.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {Object} data - The data for creating the data processor.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
export const createDataProcessorApi = async (axios, data) => {
  return await axios.post(
    `${BASEURL}/data-processor/create-data-processor`,
    data,
    {}
  );
};

/**
 * Executes a data processor with the provided data.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {Object} data - The data to execute the data processor.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const executeDataProcessorApi = async (axios, data) => {
  return await axios.post(
    `${BASEURL}/data-processor/execute-data-processor`,
    data,
    {}
  );
};

/**
 * Fetches HTML content related to a specific path and workflow ID.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} path - The path to the HTML content.
 * @param {string} workflow_id - The ID of the workflow.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
export const fetchHtmlApi = async (axios, path, workflow_id) => {
  return await axios.get(`${BASEURL}/data-processor/get-html`, {
    params: { path, workflow_id },
  });
};

/**
 * Refreshes the status of a data processor based on workflow ID.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} workflow_id - The ID of the workflow.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
export const refreshDataProcessorStatus = async (axios, workflow_id) => {
  return await axios.get(`${BASEURL}/data-processor/refresh-data-processor`, {
    params: { workflow_id },
  });
};

export const DataProcessorApi = {
  getDataProcessor,
  executeDataProcessorApi,
  refreshDataProcessorStatus,
};
